import type { ReactNode } from 'react';

import { type Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, type Node } from '@contentful/rich-text-types';
import BpkBlockquote from '@skyscanner-internal/backpack-web/bpk-component-blockquote';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import {
  BpkList,
  BpkListItem,
} from '@skyscanner-internal/backpack-web/bpk-component-list';
import {
  BpkTable,
  BpkTableBody,
  BpkTableCell,
  BpkTableHeadCell,
  BpkTableRow,
} from '@skyscanner-internal/backpack-web/bpk-component-table';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { getRel, isExternal } from '../components/RichText/utils';

const currentLandingPagesOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Node, children: ReactNode) => (
      <BpkText tagName="h1" textStyle={TEXT_STYLES.heading2}>
        {children}
      </BpkText>
    ),
    [BLOCKS.HEADING_2]: (node: Node, children: ReactNode) => (
      <BpkText tagName="h2" textStyle={TEXT_STYLES.heading3}>
        {children}
      </BpkText>
    ),
    [BLOCKS.HEADING_3]: (node: Node, children: ReactNode) => (
      <BpkText tagName="h3" textStyle={TEXT_STYLES.heading4}>
        {children}
      </BpkText>
    ),
    [BLOCKS.HEADING_4]: (node: Node, children: ReactNode) => (
      <BpkText tagName="h4" textStyle={TEXT_STYLES.heading5}>
        {children}
      </BpkText>
    ),
    [BLOCKS.HEADING_5]: (node: Node, children: ReactNode) => (
      <BpkText tagName="h5" textStyle={TEXT_STYLES.heading6}>
        {children}
      </BpkText>
    ),
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => (
      <BpkText tagName="p" textStyle={TEXT_STYLES.base}>
        {children}
      </BpkText>
    ),
    [BLOCKS.UL_LIST]: (node: Node, children: ReactNode) => (
      <BpkList>{children}</BpkList>
    ),
    [BLOCKS.OL_LIST]: (node: Node, children: ReactNode) => (
      <BpkList ordered>{children}</BpkList>
    ),
    [BLOCKS.LIST_ITEM]: (node: Node, children: ReactNode) => (
      <BpkListItem>{children}</BpkListItem>
    ),
    [BLOCKS.QUOTE]: (node: Node, children: ReactNode) => (
      <BpkBlockquote>{children}</BpkBlockquote>
    ),
    [BLOCKS.TABLE]: (node: Node, children: ReactNode) => (
      <BpkTable>
        <BpkTableBody>{children}</BpkTableBody>
      </BpkTable>
    ),
    [BLOCKS.TABLE_ROW]: (node: Node, children: ReactNode) => (
      <BpkTableRow>{children}</BpkTableRow>
    ),
    [BLOCKS.TABLE_CELL]: (node: Node, children: ReactNode) => (
      <BpkTableCell>{children}</BpkTableCell>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node: Node, children: ReactNode) => (
      <BpkTableHeadCell>{children}</BpkTableHeadCell>
    ),
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      const rawUri = node?.data?.uri;

      if (!rawUri || typeof rawUri !== 'string') {
        return <>{children}</>;
      }

      if (rawUri.startsWith('#')) {
        return <BpkLink href={rawUri}>{children}</BpkLink>;
      }

      try {
        let safeUri = rawUri.trim();
        // Trim surrounding double quotes if present
        if (safeUri.startsWith('"') && safeUri.endsWith('"')) {
          safeUri = safeUri.slice(1, -1).trim();
        }

        const url = new URL(safeUri);
        const blank = isExternal(url);
        const rel = getRel(url);

        return (
          <BpkLink href={url.href} blank={blank} rel={rel}>
            {children}
          </BpkLink>
        );
      } catch {
        // This is not ideal, but prevents throwing of
        // page-level 500 errors due to malformed content
        // coming from an external source.
        return <>{children}</>;
      }
    },
  },
};

export default currentLandingPagesOptions;
